import { useRef, useState } from 'react'
import type { Project } from '@shared/types'
import { useApi } from '../index'
import { gccDisplayName, validateGccName } from './gccDesignName'

export interface GccRenameState {
  /** Project currently being renamed; null while the modal is closed. */
  target: Project | null
  value: string
  error: string
  saving: boolean
  inputRef: React.RefObject<HTMLInputElement>
  open: (project: Project) => void
  cancel: () => void
  setValue: (v: string) => void
  confirm: () => Promise<void>
}

/**
 * Rename state shared by the GCC Dashboard and GCC Home. Saves `name` only — `client`
 * stays the client company. Re-fetches the full project before saving because list
 * queries may return a projection without gccData (CosmosDB LIST_FIELDS), and saving
 * that projection back would wipe the design.
 */
export function useGccRename(onRenamed: (project: Project) => void | Promise<void>): GccRenameState {
  const api = useApi()
  const [target, setTarget] = useState<Project | null>(null)
  const [value, setValueState] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  function open(project: Project) {
    setTarget(project)
    setValueState(gccDisplayName(project))
    setError('')
    setTimeout(() => inputRef.current?.select(), 0)
  }

  function cancel() {
    if (saving) return
    setTarget(null)
    setError('')
  }

  function setValue(v: string) {
    setValueState(v)
    setError('')
  }

  async function confirm() {
    const invalid = validateGccName(value)
    if (invalid) { setError(invalid); return }
    if (!target || saving) return
    setSaving(true)
    try {
      const fresh = await api.getProject(target.id)
      if (!fresh) throw new Error('This design no longer exists.')
      const updated: Project = { ...fresh, name: value.trim(), updatedAt: new Date().toISOString() }
      await api.saveProject(updated)
      setTarget(null)
      setError('')
      // The rename has landed; a failed list refresh must not surface as a rename error.
      await Promise.resolve(onRenamed(updated)).catch(() => {})
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename')
    } finally {
      setSaving(false)
    }
  }

  return { target, value, error, saving, inputRef, open, cancel, setValue, confirm }
}
